import { useEffect, useMemo, useRef, useState } from 'react';
import { type PendingRedemption } from '@chainflip/sdk/funding';
import { abbreviate } from '@chainflip/utils/string';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import { ethereum } from '@/shared/assets/chains';
import { validateEvmAddress } from '@/shared/assets/chains/addressValidation';
import SuccessHero from '@/shared/assets/svg/success-circular-banner.svg';
import { Link } from '@/shared/components/atoms/Link';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import AddressInputOld from '@/shared/components/PolkadotConnectionButton/AddressInputOld';
import CheckContract from '@/shared/components/PolkadotConnectionButton/assets/circular-shadow-contract-check.svg';
import RedemptionPending from '@/shared/components/PolkadotConnectionButton/RedeemFlipModal/RedemptionPending';
import TokenInputField from '@/shared/components/TokenInputField';
import { WarningModal } from '@/shared/components/WarningModal';
import useBoolean from '@/shared/hooks/useBoolean';
import useOldFundingSDK from '@/shared/hooks/useOldFundingSdk';
import {
  type InjectedAccountWithMeta,
  type ReadonlyAccount,
  usePolkadot,
} from '@/shared/hooks/usePolkadot';
import useSteps from '@/shared/hooks/useSteps';
import useToast from '@/shared/hooks/useToast';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import Information from '@/shared/icons/flip-ui-kit/large/Information';
import WindowIcon from '@/shared/icons/flip-ui-kit/small/Window';
import { PENDING_REDEMPTION } from '@/shared/queryKeys';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import type ExtrinsicSDK from '@/shared/utils/ExtrinsicSDK';
import { etherscanUrl } from '@/shared/utils/helpers';
import TokenAmount from '@/shared/utils/TokenAmount';

const ConnectWallet = () => (
  <div className="flex h-full w-full items-end justify-end">
    <ConnectButton.Custom>
      {({ openConnectModal }) => (
        <Button size="small" onClick={openConnectModal}>
          Connect Ethereum Wallet
        </Button>
      )}
    </ConnectButton.Custom>
  </div>
);

const WaitForRedemption = () => {
  const {
    selectedAccount: { idHex },
  } = usePolkadot(true);
  const { fundingSDK } = useOldFundingSDK();
  const { data: pendingRedemption } = useQuery({
    queryKey: ['pending-redemption', idHex],
    queryFn: async () => (await fundingSDK!.getPendingRedemption(idHex)) ?? null,
    refetchInterval: 5000,
    enabled: fundingSDK !== undefined,
  });

  const hasRedemption = useRef(false);

  if (pendingRedemption) {
    hasRedemption.current = true;
  }

  const toast = useToast('waiting-for-redemption');

  useEffect(() => {
    const loadingMessage = 'Waiting for redemption on Ethereum network...';
    toast.loading(loadingMessage);

    return () => {
      if (hasRedemption.current) {
        toast.success('Redemption confirmed pending on Ethereum network');
      } else {
        toast.dismiss();
      }
    };
  }, []);

  return (
    <div className="relative mt-[90px] flex h-full w-full items-center justify-center">
      <CheckContract className="pointer-events-none absolute mb-20" />
    </div>
  );
};

export default function RedeemFlipModal({
  open,
  onClose,
  account,
  flipBalance,
  redeemFlip,
  boundRedeemAddress,
}: {
  open: boolean;
  onClose: () => void;
  account: InjectedAccountWithMeta | ReadonlyAccount | null;
  flipBalance: TokenAmount | null;
  redeemFlip: typeof ExtrinsicSDK.prototype.redeemFlip;
  boundRedeemAddress?: `0x${string}`;
}) {
  const {
    selectedAccount: { idHex },
  } = usePolkadot(true);
  const ethAccount = useAccount();
  const [address, setAddress] = useState(boundRedeemAddress ?? ethAccount.address ?? '');
  const [amount, setAmount] = useState<TokenAmount>();
  const { fundingSDK, redemptionDelayInMilliseconds } = useOldFundingSDK();
  const [successTx, setSuccessTx] = useState<string>();
  const redeemToast = useToast('redeem-flip');

  const {
    value: showWarningModal,
    setTrue: openWarningModal,
    setFalse: closeWarningModal,
  } = useBoolean(false);

  const { step, advanceStep, gotoStep } = useSteps(
    'create-redemption',
    'wait-for-pending',
    'redemption-pending',
    'redemption-success',
  );
  const [redemption, setRedemption] = useState<PendingRedemption>();

  const { data: pendingRedemption } = useQuery({
    queryKey: [PENDING_REDEMPTION, idHex, Boolean(fundingSDK)],
    queryFn: async () => (await fundingSDK!.getPendingRedemption(idHex)) ?? null,
    refetchInterval: 5000,
    enabled: open && fundingSDK !== undefined,
  });

  const {
    data: redemptionTax = TokenAmount.fromWholeUnits(5),
    status,
    refetch,
  } = useQuery({
    queryFn: async () => new TokenAmount(await fundingSDK!.getRedemptionTax()),
    queryKey: ['redemptionTax'],
    enabled: fundingSDK !== undefined,
    retryDelay: 1000,
  });
  useEffect(() => {
    if (fundingSDK && status !== 'success') refetch();
  }, [fundingSDK, status]);

  const isValidAddress = useMemo(() => validateEvmAddress(address), [address]);

  const maxAmount = flipBalance ?? TokenAmount.ZERO;
  const { mutate: executeRedemption, isPending: redemptionPending } = useMutation({
    mutationFn: (redeemAmount: 'Max' | TokenAmount) => {
      redeemToast.loading('Submitting redemption request...');
      if (redeemAmount === 'Max') {
        return redeemFlip('Max', address);
      }
      return redeemFlip({ Exact: redeemAmount.toString() }, address);
    },
    onSuccess: () => {
      redeemToast.success('Redemption request submitted');
      advanceStep();
    },
    onError: (error) => {
      redeemToast.error('Error while submitting redemption request', {
        description: error.message,
      });
    },
  });

  const submitRedemption = () => {
    const isMax = maxAmount.eq(amount);
    if (isMax) {
      return openWarningModal();
    }
    return executeRedemption(amount!);
  };

  useEffect(() => {
    if (pendingRedemption) {
      setRedemption(pendingRedemption);
      gotoStep('redemption-pending');
    }
  }, [pendingRedemption, open]);

  if (!account) return null;

  let title = 'Redeem FLIP';
  let body;
  let subtitle;

  if (showWarningModal) {
    return (
      <WarningModal
        isOpen={showWarningModal}
        onAccept={() => {
          closeWarningModal();
          executeRedemption('Max');
        }}
        onClose={() => {
          closeWarningModal();
        }}
        title={title}
        content={
          <>
            <span className="text-cf-light-2">If you run out of FLIP, you will</span>
            <span className="text-cf-white">
              {' '}
              not be able to withdraw any of your current balances.
            </span>
          </>
        }
      />
    );
  }

  if (fundingSDK && step === 'create-redemption') {
    const subtext =
      amount && !amount.eq(maxAmount)
        ? `+${redemptionTax.toFixed()} ${chainflipAssetMap.Flip.symbol} Redemption tax`
        : '';
    body = (
      <>
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-center space-x-1">
            <Information className="text-cf-blue-2" />
            <span className="text-12 text-cf-white">
              If you withdraw less than your total balance, the network charges{' '}
              {redemptionTax.toFormatted()} {FLIP_SYMBOL} per redemption request to prevent spam.
            </span>
          </div>
          <TokenInputField
            token={chainflipAssetMap.Flip}
            value={amount}
            onChange={setAmount}
            balance={flipBalance ?? undefined}
            maxAmount={maxAmount}
            subtext={subtext}
          />
        </div>
        <AddressInputOld
          label="Ethereum address"
          chain={ethereum}
          address={address}
          setAddress={setAddress}
          isValidAddress={isValidAddress}
          disabled={boundRedeemAddress !== undefined}
        />
        <div className="flex w-full justify-end">
          <Button
            loading={redemptionPending && 'iconOnly'}
            disabled={!isValidAddress || !amount || amount.eq(0)}
            onClick={submitRedemption}
          >
            Redeem
          </Button>
        </div>
      </>
    );
    subtitle = 'Submit your redemption request to the State Chain to begin the redemption process.';
  } else if (step === 'wait-for-pending') {
    body = <WaitForRedemption />;
    subtitle =
      'Your redemption request was submitted successfully to the state chain. Please hang tight while we wait for it to be submitted to the Ethereum network.';
  } else if (step === 'redemption-pending' && fundingSDK && redemption && idHex) {
    body = (
      <RedemptionPending
        idHex={idHex}
        fundingSDK={fundingSDK}
        redemption={redemption}
        redemptionDelayInMilliseconds={redemptionDelayInMilliseconds}
        setSuccessTx={setSuccessTx}
        onSuccess={() => {
          gotoStep('redemption-success');
        }}
      />
    );
  } else if (step === 'redemption-success') {
    title = '';
    body = (
      <div className="relative flex h-full w-full flex-col items-center">
        <SuccessHero className="pointer-events-none absolute mt-[-76px]" />
        <div className="mt-20 flex flex-col items-center space-y-5 text-white">
          <div className="flex flex-col items-center space-y-1 font-aeonikMedium">
            You redeemed
            <div className="cf-gray-gradient flex items-center gap-x-2 font-aeonikBold text-[32px]">
              <TokenWithChainByAsset asset="Flip" size="large" />{' '}
              {new TokenAmount(redemption?.amount ?? 0).toFixed()} {chainflipAssetMap.Flip.symbol}
            </div>
            <div>to {abbreviate(address)}</div>
          </div>
          <Button onClick={onClose}>Return to Dashboard</Button>
          <Link
            target="_blank"
            href={etherscanUrl('tx', successTx as string)}
            className="flex items-center gap-x-1 text-12 text-cf-light-3"
          >
            <WindowIcon />
            View on explorer
          </Link>
        </div>
      </div>
    );
  } else {
    body = <ConnectWallet />;
    subtitle = 'Connect your Ethereum wallet to start the process.';
  }

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      afterLeave={() => {
        gotoStep('create-redemption');
        setRedemption(undefined);
      }}
    >
      <div className="flex w-[487px] flex-col items-center justify-center gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-1 flex-col space-y-2">
            <span className="font-aeonikMedium text-16 text-cf-white">{title}</span>
            <div className="text-14 text-cf-light-2">{subtitle}</div>
          </div>
          <button type="button" onClick={onClose} className="outline-cf-gray-5">
            <CloseIcon />
          </button>
        </div>
        {body}
      </div>
    </DashboardModal>
  );
}
